import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  ShieldCheck,
  KeyRound,
  Lock,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Loader2,
  ExternalLink,
  Eye,
  EyeOff,
  Copy,
  Server,
  Info,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';

/**
 * ─────────────────────────────────────────────────────────────────────────────
 * SECURE KEY VAULT
 *
 * Purpose: give the owner ONE safe place to paste a rotated secret
 * (GATEWAY_API_KEY, RESEND_API_KEY, OPENAI_API_KEY, STRIPE webhook secret,
 * etc.) so it travels DIRECTLY from the browser → Supabase edge function →
 * Supabase backend secrets store. The value is:
 *
 *   • NEVER pasted into Famous.ai chat
 *   • NEVER logged to the console
 *   • NEVER written to localStorage / sessionStorage
 *   • NEVER sent to any third-party analytics
 *   • NEVER echoed back in any error message
 *   • Cleared from React state the moment Save succeeds
 *
 * If the `set-backend-secret` edge function isn't deployed yet, this page
 * also shows the manual fallback (Supabase Dashboard → Edge Function Secrets)
 * which is ALWAYS available and doesn't require any code on our side.
 * ─────────────────────────────────────────────────────────────────────────────
 */

type SecretName =
  | 'GATEWAY_API_KEY'
  | 'RESEND_API_KEY'
  | 'OPENAI_API_KEY'
  | 'STRIPE_WEBHOOK_SECRET';

interface SecretMeta {
  label: string;
  description: string;
  expectedPrefix: string[];
  minLength: number;
  example: string;
  dashboardHint: string;
}

const SECRETS: Record<SecretName, SecretMeta> = {
  GATEWAY_API_KEY: {
    label: 'FamousPay Gateway Key',
    description:
      'The Famous.ai-issued gateway key that routes Stripe subscription calls through stripe.gateway.fastrouter.io. NOT a raw sk_live_… Stripe key.',
    expectedPrefix: ['famouspay_', 'gw_', 'fp_'],
    minLength: 32,
    example: 'famouspay_live_xxxxxxxxxxxxxxxxxxxxxxxx',
    dashboardHint:
      'In Famous → Payments → Gateway settings, click "Reveal key" and copy the full value.',
  },
  RESEND_API_KEY: {
    label: 'Resend API Key',
    description:
      'Server-scoped Resend key (starts with re_). Used by the send-test-email and send-invite edge functions.',
    expectedPrefix: ['re_'],
    minLength: 24,
    example: 're_xxxxxxxxxxxxxxxxxxxxxxxx',
    dashboardHint: 'Create or rotate at https://resend.com/api-keys (server-scoped, not client).',
  },
  OPENAI_API_KEY: {
    label: 'OpenAI API Key',
    description:
      'Optional. Only needed if ENABLE_AI_REASONING is on. Frontend never sees this — only the AI edge function reads it.',
    expectedPrefix: ['sk-'],
    minLength: 40,
    example: 'sk-proj-xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx',
    dashboardHint: 'Create at https://platform.openai.com/api-keys with the smallest scope possible.',
  },
  STRIPE_WEBHOOK_SECRET: {
    label: 'Stripe Webhook Signing Secret',
    description:
      'whsec_… value from the Stripe dashboard → Developers → Webhooks → your endpoint → Signing secret.',
    expectedPrefix: ['whsec_'],
    minLength: 32,
    example: 'whsec_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx',
    dashboardHint:
      'Stripe Dashboard → Developers → Webhooks → click your endpoint → Reveal signing secret.',
  },
};

type Banner = { kind: 'ok' | 'err' | 'info' | 'warn'; msg: string } | null;

const PRIVACY_BULLETS = [
  'The key is sent over HTTPS straight from your browser to a Supabase edge function — it never touches Famous.ai chat or any AI model.',
  'It is written to Supabase Edge Function Secrets (encrypted at rest) and immediately cleared from the form.',
  'No analytics, no console.log, no localStorage, no error message ever contains the value.',
  'If the edge function is not deployed, the form refuses to send and shows the manual dashboard fallback below.',
];

const DASHBOARD_URL =
  'https://supabase.com/dashboard/project/vadsiqsuomscnuiuyreo/settings/functions';

function maskPreview(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return '';
  if (trimmed.length <= 8) return '•'.repeat(trimmed.length);
  return `${trimmed.slice(0, 6)}…${'•'.repeat(6)}…${trimmed.slice(-4)} (${trimmed.length} chars)`;
}

const SecureKeyVault: React.FC = () => {
  const navigate = useNavigate();
  const { user, isOwner, isAdmin, isAuthenticated, loading } = useAuth();

  const [secretName, setSecretName] = useState<SecretName>('GATEWAY_API_KEY');
  const [value, setValue] = useState('');
  const [reveal, setReveal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [banner, setBanner] = useState<Banner>(null);
  const [lastSavedAt, setLastSavedAt] = useState<string | null>(null);

  const meta = SECRETS[secretName];

  const validation = useMemo(() => {
    const v = value.trim();
    if (!v) return { ok: false, reason: 'Empty — paste your rotated key.' };
    if (v.length < meta.minLength) {
      return {
        ok: false,
        reason: `Looks too short (${v.length} chars). A real ${meta.label} is at least ${meta.minLength} characters.`,
      };
    }
    if (secretName === 'GATEWAY_API_KEY') {
      if (v.startsWith('sk_live_') || v.startsWith('sk_test_')) {
        return {
          ok: false,
          reason:
            'That looks like a raw Stripe secret key (sk_…). The gateway expects its own FamousPay key, not the raw Stripe key. Rotate this in Stripe immediately and use the gateway key instead.',
        };
      }
    }
    const ok = meta.expectedPrefix.some((p) => v.startsWith(p));
    if (!ok) {
      return {
        ok: false,
        reason: `Expected the value to start with one of: ${meta.expectedPrefix.join(', ')}. Got: ${v.slice(0, 8)}…`,
      };
    }
    return { ok: true, reason: '' };
  }, [value, meta, secretName]);

  const clearValue = () => {
    setValue('');
    setReveal(false);
  };

  const handleSave = async () => {
    setBanner(null);
    if (!validation.ok) {
      setBanner({ kind: 'err', msg: validation.reason });
      return;
    }
    setSaving(true);
    try {
      // We deliberately do NOT log the value, only the secret NAME and length.
      // The edge function `set-backend-secret` is expected to:
      //   - require an authenticated owner/admin session
      //   - whitelist the secret names listed above
      //   - call the Supabase Management API to write the secret
      //   - return { ok: true } and NEVER echo the value back
      //
      // If the function isn't deployed, supabase.functions.invoke will return
      // an error and we surface the manual dashboard fallback.
      const payload = { name: secretName, value: value.trim() };
      const { data, error } = await supabase.functions.invoke('set-backend-secret', {
        body: payload,
      });

      if (error) {
        const msg = (error.message || '').toLowerCase();
        const looksMissing =
          msg.includes('not found') ||
          msg.includes('404') ||
          msg.includes('failed to fetch') ||
          msg.includes('non-2xx');
        if (looksMissing) {
          setBanner({
            kind: 'warn',
            msg:
              'The set-backend-secret edge function is not deployed on this project yet, so the key was NOT sent anywhere. ' +
              'Use the manual fallback below — paste it directly into Supabase Dashboard → Edge Function Secrets. ' +
              'Once you do, click "Re-verify" on the System Health page.',
          });
        } else {
          setBanner({
            kind: 'err',
            msg: `Edge function rejected the save: ${error.message}. The key was NOT stored. (No value is ever included in this message.)`,
          });
        }
        return;
      }

      if (data?.ok) {
        setLastSavedAt(new Date().toISOString());
        clearValue();
        setBanner({
          kind: 'ok',
          msg: `${meta.label} stored successfully in backend secrets. Open /system-health and click "Re-verify" to confirm the gateway accepts it.`,
        });
      } else {
        setBanner({
          kind: 'err',
          msg:
            data?.error ||
            'Edge function returned an unexpected response. The key was NOT stored.',
        });
      }
    } catch (e: any) {
      setBanner({
        kind: 'err',
        msg: `Unexpected error while saving: ${e?.message || 'unknown'}. The key was NOT stored.`,
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-slate-50 px-4 py-16">
        <div className="max-w-xl mx-auto bg-white border border-slate-200 rounded-2xl p-8 text-center">
          <ShieldCheck className="w-10 h-10 mx-auto text-fuchsia-600" />
          <h1 className="mt-3 text-xl font-bold text-slate-900">
            Sign in to access the Key Vault
          </h1>
          <p className="text-slate-600 mt-2">
            Only the family owner can install or rotate backend secrets.
          </p>
          <Button className="mt-5" onClick={() => navigate('/login')}>
            Sign in
          </Button>
        </div>
      </div>
    );
  }

  if (!isOwner && !isAdmin) {
    return (
      <div className="min-h-screen bg-slate-50 px-4 py-16">
        <div className="max-w-xl mx-auto bg-white border border-slate-200 rounded-2xl p-8 text-center">
          <ShieldCheck className="w-10 h-10 mx-auto text-amber-500" />
          <h1 className="mt-3 text-xl font-bold text-slate-900">Owner-only page</h1>
          <p className="text-slate-600 mt-2">
            The Key Vault writes secrets that affect billing, email, and AI delivery. Only the
            family owner or an admin can use it.
          </p>
          <Button className="mt-5" onClick={() => navigate('/')}>
            Back to home
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <button
          onClick={() => navigate('/system-health')}
          className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 text-sm font-medium mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to System Health
        </button>

        <div className="flex items-start gap-4 mb-8">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow">
            <KeyRound className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Secure Key Vault</h1>
            <p className="text-slate-600 mt-1">
              The only safe place to paste a rotated secret. Never paste keys into Famous.ai chat —
              chat messages are visible to the assistant and stored in conversation history.
            </p>
          </div>
        </div>

        {/* Privacy guarantees */}
        <div className="mb-6 bg-emerald-50 border border-emerald-200 rounded-xl p-5">
          <div className="flex items-center gap-2 mb-3">
            <Lock className="w-4 h-4 text-emerald-700" />
            <h2 className="text-sm font-bold text-emerald-900 uppercase tracking-wide">
              What this page does (and does not) do with your key
            </h2>
          </div>
          <ul className="space-y-2">
            {PRIVACY_BULLETS.map((b) => (
              <li key={b} className="flex gap-2 text-sm text-emerald-900">
                <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5 text-emerald-600" />
                <span>{b}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Danger banner if Stripe sk_ detected */}
        <div className="mb-6 bg-rose-50 border border-rose-200 rounded-xl p-5 flex gap-3">
          <AlertTriangle className="w-5 h-5 text-rose-600 shrink-0 mt-0.5" />
          <div className="text-sm text-rose-900">
            <div className="font-semibold mb-1">
              If you previously pasted a raw Stripe key (sk_live_… / sk_test_…) anywhere in chat
            </div>
            That value is now considered exposed. Open Stripe → Developers → API keys and click{' '}
            <strong>Roll key</strong> on it immediately. Then come back here with the new key — but
            remember: for FamousPay routing you want the <strong>gateway key</strong>, not the raw
            Stripe key.
          </div>
        </div>

        {/* Secret picker */}
        <div className="bg-white border border-slate-200 rounded-2xl p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Server className="w-4 h-4 text-slate-500" />
            <h2 className="text-base font-bold text-slate-900">1. Which secret are you rotating?</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {(Object.keys(SECRETS) as SecretName[]).map((name) => {
              const m = SECRETS[name];
              const active = secretName === name;
              return (
                <button
                  key={name}
                  type="button"
                  onClick={() => {
                    setSecretName(name);
                    clearValue();
                    setBanner(null);
                  }}
                  className={`text-left px-4 py-3 rounded-lg border transition ${
                    active
                      ? 'border-emerald-500 bg-emerald-50 ring-2 ring-emerald-200'
                      : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span
                      className={`text-sm font-bold ${
                        active ? 'text-emerald-900' : 'text-slate-900'
                      }`}
                    >
                      {m.label}
                    </span>
                    <code
                      className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${
                        active
                          ? 'bg-emerald-100 text-emerald-800'
                          : 'bg-slate-100 text-slate-600'
                      }`}
                    >
                      {name}
                    </code>
                  </div>
                  <p className="text-xs text-slate-600 mt-1.5 leading-relaxed">{m.description}</p>
                </button>
              );
            })}
          </div>
        </div>

        {/* Paste box */}
        <div className="bg-white border border-slate-200 rounded-2xl p-6 mb-6">
          <h2 className="text-base font-bold text-slate-900 mb-1">
            2. Paste the rotated {meta.label}
          </h2>
          <p className="text-xs text-slate-500 mb-4">
            Expected format: <code className="px-1 py-0.5 bg-slate-100 rounded">{meta.example}</code>{' '}
            · {meta.dashboardHint}
          </p>

          <div className="relative">
            <Label htmlFor="secret-value" className="sr-only">
              Secret value
            </Label>
            <textarea
              id="secret-value"
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              data-lpignore="true"
              data-1p-ignore="true"
              data-form-type="other"
              rows={3}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder={`Paste the rotated value here (it will be masked)…`}
              className={`w-full px-3 py-2.5 pr-12 rounded-lg border text-sm font-mono break-all resize-none focus:outline-none focus:ring-2 ${
                value
                  ? validation.ok
                    ? 'border-emerald-300 focus:ring-emerald-200 bg-emerald-50/30'
                    : 'border-rose-300 focus:ring-rose-200 bg-rose-50/30'
                  : 'border-slate-300 focus:ring-emerald-200'
              }`}
              style={{
                WebkitTextSecurity: reveal ? 'none' : ('disc' as any),
              } as React.CSSProperties}
            />
            <button
              type="button"
              onClick={() => setReveal((r) => !r)}
              className="absolute top-2 right-2 inline-flex items-center gap-1 px-2 py-1 rounded text-xs text-slate-600 hover:bg-slate-100"
              aria-label={reveal ? 'Hide value' : 'Reveal value'}
            >
              {reveal ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
              {reveal ? 'Hide' : 'Reveal'}
            </button>
          </div>

          {value && (
            <div className="mt-2 flex items-center justify-between text-xs">
              <span className="font-mono text-slate-500 break-all">
                Preview: {maskPreview(value)}
              </span>
              <button
                type="button"
                onClick={clearValue}
                className="text-slate-500 hover:text-rose-600"
              >
                Clear
              </button>
            </div>
          )}

          {value && !validation.ok && (
            <div className="mt-3 flex gap-2 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">
              <XCircle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>{validation.reason}</span>
            </div>
          )}
          {value && validation.ok && (
            <div className="mt-3 flex gap-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
              <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
              <span>Format looks valid. Ready to save securely.</span>
            </div>
          )}

          <div className="mt-5 flex flex-col sm:flex-row gap-2">
            <Button
              onClick={handleSave}
              disabled={saving || !validation.ok}
              className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Lock className="w-4 h-4 mr-2" />
              )}
              Save securely to backend secrets
            </Button>
            <Button variant="outline" onClick={clearValue} disabled={!value || saving}>
              Clear field
            </Button>
            <div className="flex-1" />
            {lastSavedAt && (
              <span className="self-center text-xs text-emerald-700">
                Last saved {new Date(lastSavedAt).toLocaleString()}
              </span>
            )}
          </div>

          {banner && (
            <div
              className={`mt-4 rounded-lg border px-4 py-3 text-sm ${
                banner.kind === 'ok'
                  ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                  : banner.kind === 'err'
                  ? 'bg-rose-50 border-rose-200 text-rose-800'
                  : banner.kind === 'warn'
                  ? 'bg-amber-50 border-amber-200 text-amber-900'
                  : 'bg-sky-50 border-sky-200 text-sky-800'
              }`}
            >
              {banner.msg}
            </div>
          )}
        </div>

        {/* Manual fallback */}
        <div className="bg-white border border-slate-200 rounded-2xl p-6 mb-6">
          <div className="flex items-center gap-2 mb-3">
            <Info className="w-4 h-4 text-slate-500" />
            <h2 className="text-base font-bold text-slate-900">
              3. Manual fallback (always works, no code required)
            </h2>
          </div>
          <p className="text-sm text-slate-600 mb-4">
            If the edge-function save above isn't available, you can install the rotated value
            directly in the Supabase dashboard. This path never goes through any frontend code at
            all:
          </p>
          <ol className="space-y-3 text-sm text-slate-700">
            <li className="flex gap-3">
              <span className="shrink-0 w-6 h-6 rounded-full bg-slate-900 text-white text-xs font-bold flex items-center justify-center">
                1
              </span>
              <div>
                Open{' '}
                <a
                  href={DASHBOARD_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-emerald-700 hover:underline inline-flex items-center gap-1"
                >
                  Supabase → Project Settings → Edge Functions
                  <ExternalLink className="w-3.5 h-3.5" />
                </a>
              </div>
            </li>
            <li className="flex gap-3">
              <span className="shrink-0 w-6 h-6 rounded-full bg-slate-900 text-white text-xs font-bold flex items-center justify-center">
                2
              </span>
              <div>
                Scroll to <strong>“Edge Function Secrets.”</strong> If{' '}
                <code className="px-1 py-0.5 bg-slate-100 rounded">{secretName}</code> already
                exists, click <strong>Edit</strong>. Otherwise click <strong>Add new secret</strong>.
              </div>
            </li>
            <li className="flex gap-3">
              <span className="shrink-0 w-6 h-6 rounded-full bg-slate-900 text-white text-xs font-bold flex items-center justify-center">
                3
              </span>
              <div>
                Name (exact, case-sensitive):
                <div className="mt-1 flex items-center gap-2">
                  <code className="flex-1 px-3 py-2 bg-slate-50 border border-slate-200 rounded text-xs font-mono">
                    {secretName}
                  </code>
                  <button
                    type="button"
                    onClick={() => navigator.clipboard.writeText(secretName)}
                    className="inline-flex items-center gap-1 px-2 py-2 rounded border border-slate-200 hover:bg-slate-50 text-xs"
                  >
                    <Copy className="w-3.5 h-3.5" />
                    Copy
                  </button>
                </div>
              </div>
            </li>
            <li className="flex gap-3">
              <span className="shrink-0 w-6 h-6 rounded-full bg-slate-900 text-white text-xs font-bold flex items-center justify-center">
                4
              </span>
              <div>
                Paste the rotated value, click <strong>Save</strong>, then return here (or to{' '}
                <button
                  type="button"
                  onClick={() => navigate('/system-health')}
                  className="text-emerald-700 hover:underline"
                >
                  System Health
                </button>
                ) and re-run the gateway probe to confirm it's accepted.
              </div>
            </li>
          </ol>
        </div>

        <p className="text-xs text-slate-500 text-center">
          Signed in as <code>{user?.email || '—'}</code>. This page is owner-only and is never
          indexed.
        </p>
      </div>
    </div>
  );
};

export default SecureKeyVault;
